if (!customElements.get('gallery-zoom-open')) {
  class GalleryZoomOpen extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.handleClick.bind(this));
    }

    handleClick(evt) {
      const mediaGallery = this.closest('media-gallery');
      const zoomDevice = mediaGallery.dataset.zoomEnabled;

      if (
        zoomDevice.length === 0
        || (zoomDevice === 'mobile' && !theme.mediaMatches.md)
        || (zoomDevice === 'desktop' && theme.mediaMatches.md)
      ) {
        if (!mediaGallery.galleryModal.parentElement) {
          document.body.appendChild(mediaGallery.galleryModal);
        }

        mediaGallery.galleryModal.open(evt.currentTarget);
        const zoom = mediaGallery.galleryModal.querySelector('gallery-zoom');
        zoom.init(evt.currentTarget.parentElement.dataset.mediaId);
        zoom.focus();

        evt.preventDefault();
      }
    }
  }

  window.customElements.define('gallery-zoom-open', GalleryZoomOpen);
}

/* eslint-disable max-len */

if (!customElements.get('gallery-zoom')) {
  class GalleryZoom extends HTMLElement {
    connectedCallback() {
      if (!this.initialised) {
        this.initialised = true;

        this.classList.add('gallery-zoom--pre-reveal');
        this.zoomContainer = this.querySelector('.gallery-zoom__zoom-container');
        this.thumbContainer = this.querySelector('.gallery-zoom__thumbs');
        this.controlsContainer = this.querySelector('.gallery-zoom__controls');
        this.previousBtn = this.querySelector('.gallery-zoom__prev');
        this.nextBtn = this.querySelector('.gallery-zoom__next');
        this.counterCurrent = this.querySelector('.media-ctrl__current-item');
        this.counterTotal = this.querySelector('.media-ctrl__total-items');
        this.mediaGallery = document.querySelector('.cc-main-product media-gallery');
        this.thumbSlider = this.querySelector('.gallery-zoom__thumb-slider');

        this.mouseWheelZoomMultiplier = -0.00055;
        this.trackpadZoomMultiplier = -0.0027;
        this.pinchZoomMultiplier = 0.003;
        this.touchPanModifier = 1.0;
        this.dragThreshold = 4;

        this.currentZoomImage = null;
        this.currentTransform = {
          panX: 0,
          panY: 0,
          zoom: 1
        };

        this.pinchTracking = {
          isTracking: false,
          lastPinchDistance: 0,
          lastCenterX: 0,
          lastCenterY: 0
        };

        this.touchTracking = {
          isTracking: false,
          lastTouchX: 0,
          lastTouchY: 0,
          startTouchX: 0,
          startTouchY: 0,
          hasMoved: false
        };

        this.pointerTracking = {
          isDown: false,
          isDragging: false,
          pointerId: null,
          startX: 0,
          startY: 0,
          lastX: 0,
          lastY: 0
        };

        this.ignoreClickAfterDrag = false;
        this.ignoreNextClickFromTouch = false;

        this.boundOnThumbClick = this.onThumbClick.bind(this);
        this.boundStartTrackingTouch = this.startTrackingTouch.bind(this);
        this.boundOnTouchEnd = this.onTouchEnd.bind(this);
        this.boundTrackTouchMovement = this.trackTouchMovement.bind(this);
        this.boundTrackWheel = this.trackWheel.bind(this);
        this.boundSelectPreviousThumb = this.selectPreviousThumb.bind(this);
        this.boundSelectNextThumb = this.selectNextThumb.bind(this);
        this.boundOnZoomContainerClick = this.onZoomContainerClick.bind(this);
        this.boundOnPointerDown = this.onPointerDown.bind(this);
        this.boundOnPointerMove = this.onPointerMove.bind(this);
        this.boundOnPointerUp = this.onPointerUp.bind(this);
        this.boundHandleKeyup = this.handleKeyup.bind(this);

        this.querySelectorAll('.gallery-zoom__thumb').forEach((el) => {
          el.addEventListener('click', this.boundOnThumbClick);
        });

        if (this.zoomContainer) {
          this.zoomContainer.addEventListener('touchstart', this.boundStartTrackingTouch, { passive: true });
          this.zoomContainer.addEventListener('touchend', this.boundOnTouchEnd);
          this.zoomContainer.addEventListener('touchcancel', this.boundOnTouchEnd);
          this.zoomContainer.addEventListener('touchmove', this.boundTrackTouchMovement, { passive: false });
          this.zoomContainer.addEventListener('wheel', this.boundTrackWheel, { passive: false });

          this.zoomContainer.addEventListener('click', this.boundOnZoomContainerClick);
          this.zoomContainer.addEventListener('pointerdown', this.boundOnPointerDown);
          this.zoomContainer.addEventListener('pointermove', this.boundOnPointerMove);
          this.zoomContainer.addEventListener('pointerup', this.boundOnPointerUp);
          this.zoomContainer.addEventListener('pointercancel', this.boundOnPointerUp);
          this.zoomContainer.addEventListener('pointerleave', this.boundOnPointerUp);
        }

        this.addEventListener('keyup', this.boundHandleKeyup);

        if (this.thumbContainer) {
          this.thumbContainer.addEventListener('touchmove', (evt) => evt.stopPropagation());
        }

        if (this.previousBtn) {
          this.previousBtn.addEventListener('click', this.boundSelectPreviousThumb);
        }

        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', this.boundSelectNextThumb);
        }

        new ResizeObserver(() => this.setInitialImagePosition()).observe(this);
      }

      document.documentElement.classList.add('gallery-zoom-open');
      setTimeout(() => this.classList.remove('gallery-zoom--pre-reveal'), 10);
    }

    disconnectedCallback() {
      document.documentElement.classList.remove('gallery-zoom-open');
    }

    static createEl(type, className, appendTo, innerHTML) {
      const el = document.createElement(type);
      el.className = className;

      if (appendTo) {
        appendTo.insertAdjacentElement('beforeend', el);
      }

      if (innerHTML) {
        el.innerHTML = innerHTML;
      }

      return el;
    }

    isInteractiveTarget(target) {
      return !!target?.closest(
        '.modal__close-btn, .gallery-zoom__thumb, .gallery-zoom__controls, button, a, input, select, textarea, label'
      );
    }

    getViewportWidth() {
      return this.zoomContainer?.clientWidth || this.clientWidth;
    }

    getViewportHeight() {
      return this.zoomContainer?.clientHeight || this.clientHeight;
    }

    getFitZoom() {
      if (!this.currentZoomImage || !this.currentZoomImage.naturalWidth || !this.currentZoomImage.naturalHeight) {
        return 1;
      }

      const maxZoomX = this.getViewportWidth() / this.currentZoomImage.naturalWidth;
      const maxZoomY = this.getViewportHeight() / this.currentZoomImage.naturalHeight;

      return Math.min(maxZoomX, maxZoomY, 1);
    }

    isZoomedIn() {
      return this.currentTransform.zoom > (this.getFitZoom() + 0.001);
    }

    isDesktopPointerEvent(evt) {
      return evt.pointerType === 'mouse' || evt.pointerType === 'pen';
    }

    isLikelyTrackpadWheel(evt) {
      const absX = Math.abs(evt.deltaX);
      const absY = Math.abs(evt.deltaY);

      return evt.deltaMode === 0 && (
        absX > 0
        || absY < 40
        || !Number.isInteger(evt.deltaY)
      );
    }

    getNormalisedWheelDelta(evt) {
      let deltaY = evt.deltaY;

      if (evt.deltaMode === 1) {
        deltaY *= 16;
      } else if (evt.deltaMode === 2) {
        deltaY *= this.getViewportHeight();
      }

      return deltaY;
    }

    getRelativeCoordinates(clientX, clientY) {
      const rect = this.zoomContainer.getBoundingClientRect();

      return {
        x: clientX - rect.left,
        y: clientY - rect.top
      };
    }

    updateZoomCursor() {
      if (!this.zoomContainer) return;

      this.zoomContainer.classList.toggle('is-zoomed', this.isZoomedIn());
      this.zoomContainer.classList.toggle('is-dragging', this.pointerTracking.isDragging);
    }

    init(currentMediaId) {
      if (this.mediaGallery?.dataset.mediaGroupingEnabled) {
        let visibleThumbCount = 0;

        Array.from(this.thumbContainer.children).forEach((thumb) => {
          const mediaGalleryImage = this.mediaGallery.querySelector(`.media-viewer__item[data-media-id="${thumb.dataset.mediaId}"]`);
          thumb.hidden = mediaGalleryImage?.style.display === 'none';

          if (!thumb.hidden) visibleThumbCount += 1;
        });

        if (this.counterTotal) {
          this.counterTotal.textContent = visibleThumbCount;
        }
      }

      this.selectThumb(
        [...this.thumbContainer.children].find((el) => el.dataset.mediaId === currentMediaId)
        || this.thumbContainer.firstElementChild
      );
    }

    panZoomImageFromCoordinate(inputX, inputY) {
      if (!this.currentZoomImage) return;

      const viewWidth = this.getViewportWidth();
      const viewHeight = this.getViewportHeight();

      const zoomedWidth = this.currentZoomImage.naturalWidth * this.currentTransform.zoom;
      const zoomedHeight = this.currentZoomImage.naturalHeight * this.currentTransform.zoom;

      const doPanX = zoomedWidth > viewWidth;
      const doPanY = zoomedHeight > viewHeight;

      if (doPanX || doPanY) {
        const midX = viewWidth / 2;
        const midY = viewHeight / 2;

        const offsetFromCentreX = inputX - midX;
        const offsetFromCentreY = inputY - midY;

        let finalOffsetX = 0;
        let finalOffsetY = 0;

        if (doPanX) {
          const offsetMultiplierX = ((zoomedWidth - viewWidth) / 2) / midX;
          finalOffsetX = Math.round(-offsetFromCentreX * offsetMultiplierX);
        }

        if (doPanY) {
          const offsetMultiplierY = ((zoomedHeight - viewHeight) / 2) / midY;
          finalOffsetY = Math.round(-offsetFromCentreY * offsetMultiplierY);
        }

        this.currentTransform.panX = finalOffsetX;
        this.currentTransform.panY = finalOffsetY;
        this.alterCurrentPanBy(0, 0);
        this.updateImagePosition();
      }
    }

    alterCurrentPanBy(x, y) {
      if (!this.currentZoomImage) return;

      const viewWidth = this.getViewportWidth();
      const viewHeight = this.getViewportHeight();

      this.currentTransform.panX += x;

      let panXMax = (this.currentZoomImage.naturalWidth * this.currentTransform.zoom - viewWidth) / 2.0;
      panXMax = Math.max(panXMax, 0);
      this.currentTransform.panX = Math.min(this.currentTransform.panX, panXMax);
      this.currentTransform.panX = Math.max(this.currentTransform.panX, -panXMax);

      this.currentTransform.panY += y;

      let panYMax = (this.currentZoomImage.naturalHeight * this.currentTransform.zoom - viewHeight) / 2.0;
      panYMax = Math.max(panYMax, 0);
      this.currentTransform.panY = Math.min(this.currentTransform.panY, panYMax);
      this.currentTransform.panY = Math.max(this.currentTransform.panY, -panYMax);

      this.updateImagePosition();
    }

    setCurrentTransform(panX, panY, zoom) {
      this.currentTransform.panX = panX;
      this.currentTransform.panY = panY;
      this.currentTransform.zoom = zoom;
      this.alterCurrentTransformZoomBy(0);
    }

    alterCurrentTransformZoomBy(delta) {
      if (!this.currentZoomImage) return;

      this.currentTransform.zoom += delta;

      const fitZoom = this.getFitZoom();
      this.currentTransform.zoom = Math.max(this.currentTransform.zoom, fitZoom);
      this.currentTransform.zoom = Math.min(this.currentTransform.zoom, 1.0);

      this.alterCurrentPanBy(0, 0);
      this.updateImagePosition();
      this.updateZoomCursor();
    }

    setInitialImagePosition() {
      if (!this.currentZoomImage) return;

      const viewWidth = this.getViewportWidth();
      const viewHeight = this.getViewportHeight();

      this.currentZoomImage.style.top = `${viewHeight / 2 - this.currentZoomImage.clientHeight / 2}px`;
      this.currentZoomImage.style.left = `${viewWidth / 2 - this.currentZoomImage.clientWidth / 2}px`;

      this.pointerTracking.isDown = false;
      this.pointerTracking.isDragging = false;
      this.ignoreClickAfterDrag = false;

      this.touchTracking.isTracking = false;
      this.touchTracking.hasMoved = false;
      this.pinchTracking.isTracking = false;
      this.ignoreNextClickFromTouch = false;

      this.setCurrentTransform(0, 0, this.getFitZoom());
      this.updateZoomCursor();
    }

    updateImagePosition() {
      if (!this.currentZoomImage) return;

      this.currentZoomImage.style.transform = `translate3d(${this.currentTransform.panX}px, ${this.currentTransform.panY}px, 0) scale(${this.currentTransform.zoom})`;
    }

    selectThumb(thumb) {
      let activeThumb;

      [...thumb.parentElement.children].forEach((el) => {
        if (el === thumb) {
          activeThumb = thumb;
          el.classList.add('gallery-zoom__thumb--active');
        } else {
          el.classList.remove('gallery-zoom__thumb--active');
        }
      });

      const visibleThumbs = Array.from(activeThumb.parentElement.children).filter((child) => !child.hidden);
      const activeThumbIndex = visibleThumbs.indexOf(activeThumb);

      if (this.counterCurrent) {
        this.counterCurrent.textContent = activeThumbIndex + 1;
      }

      this.scrollToThumb(activeThumb);

      if (this.previousBtn) {
        this.previousBtn.disabled = activeThumbIndex === 0;
      }

      if (this.nextBtn) {
        this.nextBtn.disabled = activeThumbIndex === (visibleThumbs.length - 1);
      }

      this.zoomContainer.classList.add('gallery-zoom__zoom-container--loading');

      this.currentZoomImage = GalleryZoom.createEl('img', 'gallery-zoom__zoom-image');
      this.currentZoomImage.alt = thumb.querySelector('.gallery-zoom__thumb-img')?.alt || '';
      this.currentZoomImage.style.visibility = 'hidden';

      this.currentZoomImage.onload = () => {
        this.zoomContainer.classList.remove('gallery-zoom__zoom-container--loading');
        this.currentZoomImage.style.visibility = '';
        this.setInitialImagePosition();
      };

      this.currentZoomImage.src = thumb.dataset.zoomUrl;
      this.zoomContainer.replaceChildren(this.currentZoomImage);
    }

    scrollToThumb(thumb) {
      if (!this.thumbSlider || !thumb) return;

      const thumbSliderRect = this.thumbSlider.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();

      const thumbLeft = thumbRect.left - thumbSliderRect.left;
      const thumbRight = thumbRect.right - thumbSliderRect.right;

      if (thumbLeft < 0) {
        this.thumbSlider.scrollLeft += thumbLeft - (this.thumbSlider.clientWidth - thumbRect.width);
      } else if (thumbRight > 0) {
        this.thumbSlider.scrollLeft += thumbRight + (this.thumbSlider.clientWidth - thumbRect.width);
      }
    }

    selectPreviousThumb(evt) {
      if (evt) evt.preventDefault();
      if (this.thumbContainer.childElementCount < 2) return;

      let previous = this.thumbContainer.querySelector('.gallery-zoom__thumb--active').previousElementSibling;

      while (!previous || !previous.offsetParent) {
        if (!previous) {
          previous = this.thumbContainer.lastElementChild;
        } else {
          previous = previous.previousElementSibling;
        }
      }

      this.selectThumb(previous);
    }

    selectNextThumb(evt) {
      if (evt) evt.preventDefault();
      if (this.thumbContainer.childElementCount < 2) return;

      let next = this.thumbContainer.querySelector('.gallery-zoom__thumb--active').nextElementSibling;

      while (!next || !next.offsetParent) {
        if (!next) {
          next = this.thumbContainer.firstElementChild;
        } else {
          next = next.nextElementSibling;
        }
      }

      this.selectThumb(next);
    }

    zoomTowardsPoint(clientX, clientY, delta) {
      if (!this.currentZoomImage) return;

      const oldZoom = this.currentTransform.zoom;
      const fitZoom = this.getFitZoom();

      let newZoom = oldZoom + delta;
      newZoom = Math.max(newZoom, fitZoom);
      newZoom = Math.min(newZoom, 1.0);

      if (Math.abs(newZoom - oldZoom) < 0.0001) return;

      const point = this.getRelativeCoordinates(clientX, clientY);
      const viewWidth = this.getViewportWidth();
      const viewHeight = this.getViewportHeight();

      const cursorOffsetX = point.x - (viewWidth / 2);
      const cursorOffsetY = point.y - (viewHeight / 2);

      const zoomRatio = newZoom / oldZoom;

      this.currentTransform.panX = cursorOffsetX - ((cursorOffsetX - this.currentTransform.panX) * zoomRatio);
      this.currentTransform.panY = cursorOffsetY - ((cursorOffsetY - this.currentTransform.panY) * zoomRatio);
      this.currentTransform.zoom = newZoom;

      this.alterCurrentPanBy(0, 0);
      this.updateImagePosition();
      this.updateZoomCursor();
    }

    startTrackingTouch(evt) {
      if (!this.currentZoomImage) return;
      if (this.isInteractiveTarget(evt.target)) return;

      if (evt.touches.length === 1) {
        const touch = evt.touches[0];

        this.touchTracking.isTracking = true;
        this.touchTracking.lastTouchX = touch.clientX;
        this.touchTracking.lastTouchY = touch.clientY;
        this.touchTracking.startTouchX = touch.clientX;
        this.touchTracking.startTouchY = touch.clientY;
        this.touchTracking.hasMoved = false;

        this.pinchTracking.isTracking = false;
      } else if (evt.touches.length === 2) {
        const touch1 = evt.touches[0];
        const touch2 = evt.touches[1];

        const centerX = (touch1.clientX + touch2.clientX) / 2;
        const centerY = (touch1.clientY + touch2.clientY) / 2;
        const pinchDistance = Math.hypot(
          touch1.clientX - touch2.clientX,
          touch1.clientY - touch2.clientY
        );

        this.pinchTracking.isTracking = true;
        this.pinchTracking.lastPinchDistance = pinchDistance;
        this.pinchTracking.lastCenterX = centerX;
        this.pinchTracking.lastCenterY = centerY;

        this.touchTracking.hasMoved = true;
      }
    }

    onTouchEnd(evt) {
      if (!this.currentZoomImage) return;
      if (this.isInteractiveTarget(evt.target)) return;

      if (evt.touches.length === 0) {
        const touch = evt.changedTouches && evt.changedTouches[0];
        const wasTap = touch && !this.touchTracking.hasMoved && !this.pinchTracking.isTracking;

        if (wasTap) {
          evt.preventDefault();

          const point = this.getRelativeCoordinates(touch.clientX, touch.clientY);

          if (this.isZoomedIn()) {
            this.setCurrentTransform(0, 0, this.getFitZoom());
          } else {
            this.setCurrentTransform(0, 0, 1);
            this.panZoomImageFromCoordinate(point.x, point.y);
          }

          this.ignoreNextClickFromTouch = true;
        }

        this.touchTracking.isTracking = false;
        this.touchTracking.hasMoved = false;
        this.pinchTracking.isTracking = false;
        return;
      }

      if (evt.touches.length === 1) {
        const touch = evt.touches[0];

        this.touchTracking.isTracking = true;
        this.touchTracking.lastTouchX = touch.clientX;
        this.touchTracking.lastTouchY = touch.clientY;
        this.touchTracking.startTouchX = touch.clientX;
        this.touchTracking.startTouchY = touch.clientY;
        this.touchTracking.hasMoved = true;

        this.pinchTracking.isTracking = false;
      }
    }

    trackTouchMovement(evt) {
      if (!this.currentZoomImage) return;
      if (this.isInteractiveTarget(evt.target)) return;

      evt.preventDefault();

      if (evt.touches.length === 1) {
        const touch1 = evt.touches[0];

        if (!this.touchTracking.isTracking) {
          this.touchTracking.isTracking = true;
          this.touchTracking.lastTouchX = touch1.clientX;
          this.touchTracking.lastTouchY = touch1.clientY;
          this.touchTracking.startTouchX = touch1.clientX;
          this.touchTracking.startTouchY = touch1.clientY;
          this.touchTracking.hasMoved = false;
        } else {
          const dx = touch1.clientX - this.touchTracking.lastTouchX;
          const dy = touch1.clientY - this.touchTracking.lastTouchY;

          const totalDx = touch1.clientX - this.touchTracking.startTouchX;
          const totalDy = touch1.clientY - this.touchTracking.startTouchY;

          if (Math.hypot(totalDx, totalDy) >= this.dragThreshold) {
            this.touchTracking.hasMoved = true;
          }

          if (this.isZoomedIn()) {
            this.alterCurrentPanBy(
              dx * this.touchPanModifier,
              dy * this.touchPanModifier
            );
          }

          this.touchTracking.lastTouchX = touch1.clientX;
          this.touchTracking.lastTouchY = touch1.clientY;
        }

        this.pinchTracking.isTracking = false;
        return;
      }

      if (evt.touches.length === 2) {
        const touch1 = evt.touches[0];
        const touch2 = evt.touches[1];

        const centerX = (touch1.clientX + touch2.clientX) / 2;
        const centerY = (touch1.clientY + touch2.clientY) / 2;
        const pinchDistance = Math.hypot(
          touch1.clientX - touch2.clientX,
          touch1.clientY - touch2.clientY
        );

        this.touchTracking.hasMoved = true;

        if (!this.pinchTracking.isTracking) {
          this.pinchTracking.isTracking = true;
          this.pinchTracking.lastPinchDistance = pinchDistance;
          this.pinchTracking.lastCenterX = centerX;
          this.pinchTracking.lastCenterY = centerY;
        } else {
          const pinchDelta = pinchDistance - this.pinchTracking.lastPinchDistance;
          const zoomDelta = pinchDelta * this.pinchZoomMultiplier;

          if (zoomDelta !== 0) {
            this.zoomTowardsPoint(centerX, centerY, zoomDelta);
          }

          if (this.isZoomedIn()) {
            this.alterCurrentPanBy(
              centerX - this.pinchTracking.lastCenterX,
              centerY - this.pinchTracking.lastCenterY
            );
          }

          this.pinchTracking.lastPinchDistance = pinchDistance;
          this.pinchTracking.lastCenterX = centerX;
          this.pinchTracking.lastCenterY = centerY;
        }
      }
    }

    trackWheel(evt) {
      if (!this.currentZoomImage) return;
      if (this.isInteractiveTarget(evt.target)) return;

      evt.preventDefault();

      const normalisedDeltaY = this.getNormalisedWheelDelta(evt);
      const isTrackpad = this.isLikelyTrackpadWheel(evt);
      const multiplier = isTrackpad
        ? this.trackpadZoomMultiplier
        : this.mouseWheelZoomMultiplier;

      let zoomDelta = normalisedDeltaY * multiplier;
      zoomDelta = Math.max(Math.min(zoomDelta, 0.12), -0.12);

      if (zoomDelta !== 0) {
        this.zoomTowardsPoint(evt.clientX, evt.clientY, zoomDelta);
      }
    }

    onPointerDown(evt) {
      if (!this.currentZoomImage) return;
      if (!this.isDesktopPointerEvent(evt)) return;
      if (!this.isZoomedIn()) return;

      this.pointerTracking.isDown = true;
      this.pointerTracking.isDragging = false;
      this.pointerTracking.pointerId = evt.pointerId;
      this.pointerTracking.startX = evt.clientX;
      this.pointerTracking.startY = evt.clientY;
      this.pointerTracking.lastX = evt.clientX;
      this.pointerTracking.lastY = evt.clientY;

      this.zoomContainer.setPointerCapture(evt.pointerId);
      this.updateZoomCursor();
      evt.preventDefault();
    }

    onPointerMove(evt) {
      if (!this.currentZoomImage) return;
      if (!this.isDesktopPointerEvent(evt)) return;
      if (!this.pointerTracking.isDown) return;
      if (evt.pointerId !== this.pointerTracking.pointerId) return;

      const totalDx = evt.clientX - this.pointerTracking.startX;
      const totalDy = evt.clientY - this.pointerTracking.startY;

      if (!this.pointerTracking.isDragging && Math.hypot(totalDx, totalDy) >= this.dragThreshold) {
        this.pointerTracking.isDragging = true;
        this.updateZoomCursor();
      }

      const dx = evt.clientX - this.pointerTracking.lastX;
      const dy = evt.clientY - this.pointerTracking.lastY;

      if (this.pointerTracking.isDragging) {
        this.alterCurrentPanBy(dx, dy);
      }

      this.pointerTracking.lastX = evt.clientX;
      this.pointerTracking.lastY = evt.clientY;

      evt.preventDefault();
    }

    onPointerUp(evt) {
      if (!this.isDesktopPointerEvent(evt)) return;

      if (this.pointerTracking.pointerId !== null && evt.pointerId === this.pointerTracking.pointerId) {
        if (this.zoomContainer.hasPointerCapture(evt.pointerId)) {
          this.zoomContainer.releasePointerCapture(evt.pointerId);
        }

        if (this.pointerTracking.isDragging) {
          this.ignoreClickAfterDrag = true;
        }
      }

      this.pointerTracking.isDown = false;
      this.pointerTracking.isDragging = false;
      this.pointerTracking.pointerId = null;

      this.updateZoomCursor();
    }

    onThumbClick(evt) {
      evt.preventDefault();
      this.selectThumb(evt.currentTarget);
    }

    onZoomContainerClick(evt) {
      if (!this.currentZoomImage) return;

      if (this.ignoreNextClickFromTouch) {
        this.ignoreNextClickFromTouch = false;
        evt.preventDefault();
        return;
      }

      evt.preventDefault();

      if (this.ignoreClickAfterDrag) {
        this.ignoreClickAfterDrag = false;
        return;
      }

      const point = this.getRelativeCoordinates(evt.clientX, evt.clientY);

      if (this.isZoomedIn()) {
        this.setCurrentTransform(0, 0, this.getFitZoom());
      } else {
        this.setCurrentTransform(0, 0, 1);
        this.panZoomImageFromCoordinate(point.x, point.y);
      }
    }

    handleKeyup(evt) {
      switch (evt.key) {
        case 'ArrowLeft':
          evt.preventDefault();
          this.selectPreviousThumb();
          break;
        case 'ArrowRight':
          evt.preventDefault();
          this.selectNextThumb();
          break;
        default:
          break;
      }
    }
  }

  window.customElements.define('gallery-zoom', GalleryZoom);
}